<template>
  <ModuleContainer />
</template>

<script>
import sdk from "@/api/sdk";

import ModuleMixin from "@/components/Map/ModuleMixin";

function parseBounds(boundsArray) {
  const coords = Array.isArray(boundsArray[0][0]) ? boundsArray[0] : boundsArray;
  const lats = coords.map(c => c[0]);
  const lngs = coords.map(c => c[1]);
  return {
    south: Math.min(...lats),
    north: Math.max(...lats),
    west: Math.min(...lngs),
    east: Math.max(...lngs)
  };
}

function isPointInBounds(lat, lng, bounds) {
  return lat >= bounds.south && lat <= bounds.north && lng >= bounds.west && lng <= bounds.east;
}

const OVERLAY_BOUNDS_MAP = (window.config.MAP.OVERLAYS || [])
  .filter(o => !o.name.endsWith(" Label") && !o.name.endsWith(" Image"))
  .reduce((map, o) => {
    map[o.name] = parseBounds(o.bounds);
    return map;
  }, {});

export default {
  mixins: [ModuleMixin],

  props: {
    value: {
      type: Array,
      required: true
    }
  },

  data() {
    return {
      utilities: null,

      categories: [],
      loading: false,

      pointVisible: true,
      visibleOverlayBounds: []
    };
  },

  computed: {
    currentUtilities: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      }
    },

    filteredUtilities() {
      if (!this.utilities) return;

      if (this.visibleOverlayBounds.length === 0) return [];

      return this.utilities.filter(u =>
        this.visibleOverlayBounds.some(bounds =>
          isPointInBounds(u.latitude, u.longitude, bounds)
        )
      );
    }
  },

  watch: {
    filteredUtilities(val) {
      if (!val) return;

      this.currentUtilities = [...val];
    }
  },

  async created() {
    await this.fetchCategories();
    await this.fetchUtilities();
  },

  methods: {
    onInit() {
      this.visibleOverlayBounds = (window.config.MAP.OVERLAYS || [])
        .filter(o => o.visible && OVERLAY_BOUNDS_MAP[o.name])
        .map(o => OVERLAY_BOUNDS_MAP[o.name]);

      this._onOverlayAdd = ({ name }) => {
        if (OVERLAY_BOUNDS_MAP[name]) {
          this.visibleOverlayBounds = [
            ...this.visibleOverlayBounds,
            OVERLAY_BOUNDS_MAP[name]
          ];
        }
      };

      this._onOverlayRemove = ({ name }) => {
        if (OVERLAY_BOUNDS_MAP[name]) {
          const removed = OVERLAY_BOUNDS_MAP[name];
          this.visibleOverlayBounds = this.visibleOverlayBounds.filter(
            b => b !== removed
          );
        }
      };

      this.map.on("overlayadd", this._onOverlayAdd);
      this.map.on("overlayremove", this._onOverlayRemove);
    },

    onDestroy() {
      if (this._onOverlayAdd) this.map.off("overlayadd", this._onOverlayAdd);
      if (this._onOverlayRemove) this.map.off("overlayremove", this._onOverlayRemove);
    },

    async fetchCategories() {
      const res = await sdk.request({
        url: "/all-categories"
      });

      this.categories = res.data.data;
    },

    async fetchUtilities() {
      try {
        this.loading = true;

        this.utilities = undefined;

        const res = await sdk.request({
          url: "/utilities"
        });

        const categoryDict = this.categories.reduce((dict, c) => {
          dict[c.id] = c;

          return dict;
        }, {});

        this.utilities = res.data.data.map(u => {
          u.category = categoryDict[u.category_id];

          return u;
        });
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped></style>
